import React, { useState, useEffect } from "react";
import { ReactComponent as SGFlag } from "../../../assets/svg/flags/singapore.svg";
import { ReactComponent as AUFlag } from "../../../assets/svg/flags/australia.svg";
import { ReactComponent as IndFlag } from "../../../assets/svg/flags/india.svg";
import { ReactComponent as PhFlag } from "../../../assets/svg/flags/phillipines.svg";

interface Country {
	name: string;
	shortCode: string;
	flag: React.ReactNode;
}

const countries: Country[] = [
	{
		name: "Singapore",
		shortCode: "SG",
		flag: <SGFlag className="w-6 h-6 rounded-md" />,
	},
	{
		name: "Australia",
		shortCode: "AU",
		flag: <AUFlag className="w-6 h-6 rounded-md" />,
	},
	{
		name: "India",
		shortCode: "IN",
		flag: <IndFlag className="w-6 h-6 rounded-md" />,
	},
	{
		name: "Phillipines",
		shortCode: "PH",
		flag: <PhFlag className="w-6 h-6 rounded-md" />,
	},
];

interface DropdownCountryProps {
	enableSearch?: boolean; // Toggle for search bar
	selectedShortCode?: string; // Preselected short code
	onSelect: (selected: string) => void; // Callback to send selected shortCode to parent
}

const DropdownCountry: React.FC<DropdownCountryProps> = ({
	enableSearch = true,
	selectedShortCode,
	onSelect,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState<string | null>(
		selectedShortCode || null
	);
	const [searchQuery, setSearchQuery] = useState("");

	useEffect(() => {
		if (selectedShortCode) {
			setSelectedCountry(selectedShortCode);
		}
	}, [selectedShortCode]);

	const toggleDropdown = () => setIsOpen((prev) => !prev);

	const handleCountrySelect = (shortCode: string) => {
		setSelectedCountry(shortCode);
		onSelect(shortCode); // Send the selected shortCode to the parent
		setIsOpen(false); // Close the dropdown
	};

	// Filter countries based on the search query
	const filteredCountries = countries.filter((country) =>
		country.name.toLowerCase().includes(searchQuery.toLowerCase())
	);

	// Helper function to get the country details by shortCode
	const getCountryDetails = (shortCode: string) => {
		return countries.find((c) => c.shortCode === shortCode);
	};

	return (
		<div className="relative inline-block text-left h-full">
			<button
				id="dropdownSearchButton"
				onClick={toggleDropdown}
				className="text-white focus:ring-4 focus:outline-none bg-slate-100 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center gap-2 h-fit"
				type="button"
			>
				{/* Display selected country and its name */}
				{selectedCountry ? (
					<div className="flex items-center gap-2">
						{getCountryDetails(selectedCountry)?.flag}
						<span className="font-Poppins font-semibold text-gray-900">
							{getCountryDetails(selectedCountry)?.name}
						</span>
					</div>
				) : (
					"Select"
				)}
				<svg
					className="w-2.5 h-2.5 ms-3"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 10 6"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="m1 1 4 4 4-4"
					/>
				</svg>
			</button>

			{isOpen && (
				<div
					id="dropdownSearch"
					className="z-10 absolute rounded-lg shadow w-60 bg-gray-700"
				>
					{enableSearch && (
						<div className="p-3">
							<input
								type="text"
								id="input-group-search"
								className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
								placeholder="Search country"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
					)}
					<ul className="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-200">
						{filteredCountries.map((country) => (
							<li key={country.shortCode} className="py-1">
								<div
									className="flex items-center ps-2 rounded hover:bg-gray-600 cursor-pointer gap-4 p-2"
									onClick={() =>
										handleCountrySelect(country.shortCode)
									}
								>
									{country.flag}
									<span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300 tracking-wider">
										{country.name}
									</span>
									{selectedCountry === country.shortCode && (
										<svg
											className="w-4 h-4 text-green-500 ml-auto"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth="2"
											stroke="currentColor"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									)}
								</div>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default DropdownCountry;
